import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';
import { Route, Routes, BrowserRouter, Navigate } from 'react-router-dom';
import axios from 'axios';

import Sidebar from '../layout/sidebar';
import Clients from '../modules/private/clients';
import ClientsDetails from '../modules/private/clients-details';
import CollaboratorDetails from '../modules/private/collaborator-details';
import Profile from '../modules/private/profile';
import Users from '../modules/private/users';
import RecoverPass from '../modules/public/recover-pass';
import Login from '../modules/public/login';
import Test from '../modules/public/test';

import { ROUTES, HOST_GROWME, MAINTENANCE_MODE } from '../helpers/constants';
import Maintenance from '../modules/public/maintenance';
import { loginAtom, userAtom} from '../services/atoms/auth.atoms';
import CustomChart from '../modules/private/collaborator-details/components/custom-chart';
import Configurations from '../modules/private/configurations';
import Params from '../modules/private/params/params';
import ChargesDetail from '../modules/private/params/components/charges-detail';
import SelectionProcess from '../modules/private/selection-process';
import CreateSelectionProcess from '../modules/private/selection-process/components/create-selection-process';
import DetailSelectionProcess from '../modules/private/selection-process/components/details-selection-process';
import Purchase from '../modules/private/purchase';
import Checkout from '../modules/private/checkout';
import PaymentSuccess from '../modules/private/payment-result/success';
import PaymentFailure from '../modules/private/payment-result/failure';
import ZendeskWidget  from '../components/widgets/zendesk-widget';
import ZendeskDebug from '../components/debug/zendesk-debug';
import PublicCheckout from '../modules/public/public-checkout';
import PaymentSuccessGuest from '../modules/public/payment-result/success-guest';
import PaymentFailureGuest from '../modules/public/payment-result/failure-guest';
import GuestPayments from '../modules/private/guest-payments';
import { PASSWORD_CHANGE_REQUIRED_EVENT, SESSION_EXPIRED_EVENT } from '../utils/session-expired';
import ForcePasswordChange from '../modules/private/force-password-change';
import AcceptInvitation from '../modules/public/invitation';

const showZendeskDebug = process.env.REACT_APP_SHOW_ZENDESK_DEBUG === 'true';

interface UserData {
  loading: boolean;
  data: any | null;
  error: Error | null;
}

const initialUserData: UserData = {
  loading: false,
  data: null,
  error: null
}

const AllRoutes: React.FC = () => {
  const [login, setLogin] = useRecoilState(loginAtom);
  const [authChecked, setAuthChecked] = useState(false);
  let user: UserData = initialUserData;
  const [userState, setUserAtomState] = useRecoilState(userAtom);
  const mustChangePassword = login.signed && userState?.data?.mustChangePassword === true;

  const getUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLogin((prevValues) => ({ ...prevValues, signed: false }));
      setAuthChecked(true);
      return;
    }

    try {
      const response = await axios.post(`${HOST_GROWME}/validateSession`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.valid && response.data.user) {
        const userData = response.data.user;
        const transformedData = {
          id: userData.id || userData.username,
          displayName: userData.name || `${userData.name || ''} ${userData.family_name || ''}`,
          name: userData.name || '',
          family_name: userData.family_name || '',
          email: userData.email || '',
          photoUrl:'https://cdn-icons-png.flaticon.com/512/149/149071.png',
          role: 'Administrador',
          group: 'Grow Me',
          phone_number: userData.phone_number || '',
          'custom:surname': userData.family_name || '',
          clientId: userData.company?.id || '',
          isAdmin: userData.isAdmin || false,
          isSuperAdmin: userData.isSuperAdmin || false,
          isAdminBasic: userData.isBasic || false,
          mustChangePassword: userData.mustChangePassword === true
        };

        setUserAtomState((prevValues) => ({
          ...prevValues,
          data: {
            ...prevValues.data,
            ...transformedData
          }
        }));
        setLogin((prevValues) => ({ ...prevValues, signed: true }));
      } else {
        localStorage.removeItem('token');
        setLogin((prevValues) => ({ ...prevValues, signed: false }));
      }
    } catch (error: any) {
      console.error('Session validation error:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        setLogin((prevValues) => ({ ...prevValues, signed: false }));
      }
    } finally {
      setAuthChecked(true);
    }
  }

  // Initial session check on mount
  useEffect(() => {
    getUser();
    // eslint-disable-next-line
  }, []); // Only run on mount

  // Sesión expirada en medio de la navegación (401 del back): volver al login.
  // Con signed=false las rutas privadas redirigen solas a la pantalla de login.
  useEffect(() => {
    const handleSessionExpired = () => {
      setLogin((prevValues) => ({ ...prevValues, loading: false, signed: false }));
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    // eslint-disable-next-line
  }, []);

  // El back rechazó una acción porque falta el cambio de contraseña obligatorio
  // (p. ej. una sesión abierta antes de que existiera): mostrar esa pantalla.
  useEffect(() => {
    const handlePasswordChangeRequired = () => {
      setUserAtomState((prevValues) => ({
        ...prevValues,
        data: { ...prevValues.data, mustChangePassword: true }
      }));
    };
    window.addEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, handlePasswordChangeRequired);
    return () => window.removeEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, handlePasswordChangeRequired);
    // eslint-disable-next-line
  }, []);

  // Re-validate when login state changes (for refresh scenarios)
  useEffect(() => {
    if (login.updating) {
      getUser();
    }
    // eslint-disable-next-line
  }, [login.updating]);

  if (!authChecked) return null;

  const maintenanceBypass = localStorage.getItem('maintenanceModeByPass') === '3IRJmvMQCz155bvfOO';
  if (MAINTENANCE_MODE && !maintenanceBypass) {
    return <Maintenance />;
  }

  return (
    <>
    { login.signed && <ZendeskWidget position="right" color="#1f73b7" />}
    { ( login.signed && showZendeskDebug ) && <ZendeskDebug /> }
    <Routes>
      {mustChangePassword &&
        <Route path="/*" element={<ForcePasswordChange />} />
      }
      {login.signed && !mustChangePassword &&
        <Route element={<Sidebar user={user} />} path={ROUTES.ROOT.route}>
          <Route index element={<Clients />} />
          <Route element={<Clients />} path={ROUTES.CLIENTS.route} />
          <Route element={<ClientsDetails />} path={`${ROUTES.CLIENTS.route}/:id`} />
          <Route element={<CollaboratorDetails />} path={`${ROUTES.CLIENTS.route}/collaborator/:id`} />
          <Route element={<Profile />} path={ROUTES.PROFILE.route} />
          <Route element={<Users />} path={ROUTES.USERS.route} />
          <Route element={<Configurations />} path={ROUTES.CONFIGURATION.route} />
          <Route element={<Params />} path={ROUTES.PARAMS.route} />
          <Route element={<ChargesDetail />}  path={`${ROUTES.PARAMS.route}/charges/:id`} />
          <Route element={<SelectionProcess /> } path={ROUTES.PROCESS.route} />
          <Route element={<CreateSelectionProcess />} path={`${ROUTES.PROCESS.route}/create-view`} />
          <Route element={<DetailSelectionProcess />} path={`${ROUTES.PROCESS.route}/:id`} />
          <Route element={<CollaboratorDetails />} path={`${ROUTES.PROCESS.route}/collaborator/:id`} />
          <Route element={<Purchase />} path={ROUTES.PURCHASE.route} />
          <Route element={<Checkout />} path={ROUTES.CHECKOUT.route} />
          <Route element={<PaymentSuccess />} path={ROUTES.PAYMENT_SUCCESS.route} />
          <Route element={<PaymentFailure />} path={ROUTES.PAYMENT_FAILURE.route} />
          <Route element={<GuestPayments />} path={ROUTES.GUEST_PAYMENTS.route} />
          <Route path="/*" element={<Navigate to={ROUTES.CLIENTS.route} />} />
        </Route>
      }
      {!login.signed && 
        <>
          <Route index element={<Login />} />
          <Route element={<Login />} path={ROUTES.LOGIN.route} />
          <Route element={<RecoverPass />} path={ROUTES.RECOVER.route} />
          <Route element={<PaymentSuccessGuest />} path={ROUTES.PAYMENT_SUCCESS.route} />
          <Route element={<PaymentFailureGuest />} path={ROUTES.PAYMENT_FAILURE.route} />
          <Route path="/*" element={<Navigate to={ROUTES.ROOT.route} />} />
        </>
      }
      <Route element={<CustomChart />} path='/graphic' />
      <Route index element={<Test />} path='/tests/start' />
      {/* Activar cuenta invitada: pública, con o sin sesión en el navegador */}
      <Route element={<AcceptInvitation />} path='/invitacion' />
      <Route element={<PublicCheckout />} path={ROUTES.PUBLIC_CHECKOUT.route} />
    </Routes>
    </>  
  )
}

const GrowMe: React.FC = () => (
  <BrowserRouter>
    <AllRoutes />
  </BrowserRouter>
);

export default GrowMe;